import React, { useCallback, useMemo, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
  UserNotificationSetting,
  NotificationSettingType,
  useUpdateClinicSettingsNotificationsUserSettingMutation,
  useGetClinicSettingsNotificationsUserSettingsQuery,
  ChannelSelectionUncheckedUpdateWithoutUserSettingInput,
} from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { DEFAULT_AUDIO_URL } from 'shared/utils/sounds';
import { RouteDefinitions, SettingsRoutes } from 'routes';
import useClinicUser from 'shared/hooks/useClinicUser';
import { getChannelUnreadMessageCount } from 'shared/hooks/useUnreadMessageCount';
import {
  Icon,
  Popover,
  Flex,
  Text,
  HStack,
  Link,
  Box,
  Alert,
  useToast,
  Switch,
  Divider,
  RadioGroup,
  RadioProps,
  Checkbox,
  CheckboxGroup,
  CheckboxProps,
} from '@televet/kibble-ui';
import { useAuth } from 'shared/providers/AuthProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsMyNotificationEvents } from 'shared/providers/TrackingProvider/EventNames';

const notificationTypes = Object.values(NotificationSettingType);
const notificationTypeDisplayMapper: Record<NotificationSettingType, string> = {
  [NotificationSettingType.AppointmentConfirmed]: 'Client confirms appointment',
  [NotificationSettingType.AppointmentRescheduleRequested]: 'Client requests reschedule',
  [NotificationSettingType.ConversationAssigned]: 'Conversation assigned to me',
  [NotificationSettingType.ConversationNewMessage]: 'New message in conversation',
  [NotificationSettingType.ConversationStatusChange]: 'Conversation status updated',
  [NotificationSettingType.FormSubmitted]: 'Client submits form',
  [NotificationSettingType.PaymentReceived]: 'Payment received from client',
  [NotificationSettingType.PaymentDispute]: 'Payment dispute created',
  [NotificationSettingType.ConnectWidgetRequestSubmitted]: 'Website visitor submits Website Widget request',
};

const returnMappedSettings = (notificationSettings: UserNotificationSetting[]): UserNotificationSetting[] => {
  return notificationTypes
    .filter((type) => type !== NotificationSettingType.AppointmentRescheduleRequested)
    .map((key) => {
      return (
        notificationSettings.find((setting) => {
          return setting?.settingType === key;
        }) ||
        ({
          settingType: key,
          emailEnabled: true,
          smsEnabled: false,
          pushNotificationEnabled: false,
          soundNotificationUrl: null,
          browserNotificationEnabled: false,
        } as UserNotificationSetting)
      );
    })
    .sort((a, b) => a?.settingType.localeCompare(b?.settingType));
};

const ALL_CONVERSATIONS = 'ALL_CONVERSATIONS';
const ASSIGNED_CONVERSATIONS = 'ASSIGNED_CONVERSATIONS';
const UNASSIGNED_CONVERSATIONS = 'UNASSIGNED_CONVERSATIONS';

const successToastProps = {
  title: 'Success!',
  description: 'Your notification preferences have been updated.',
};

const inAppNotificationOptions: RadioProps[] = [
  {
    children: 'All conversations',
    value: ALL_CONVERSATIONS,
  },
  {
    children: 'Conversations assigned to:',
    value: ASSIGNED_CONVERSATIONS,
  },
];

const MyNotificationsSettings = (): JSX.Element => {
  const { clinicUser, hasAdminRoleAtCurrentClinic } = useClinicUser();
  const toast = useToast();
  const { isImpersonating } = useAuth();
  const [shouldShowPermissionsAlert, setShouldShowPermissionsAlert] = useState(false);
  const notificationPermissionStatus = 'Notification' in window ? Notification.permission : 'denied';
  const { trackEvent } = useTracking();

  const { data: userData } = useGetClinicSettingsNotificationsUserSettingsQuery({
    variables: { where: { id: clinicUser?.id || '' } },
    skip: !clinicUser,
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  const settings = useMemo(() => {
    const mappedSettings = returnMappedSettings(userData?.findUniqueUser?.userSetting?.notificationSettings || []);

    return hasAdminRoleAtCurrentClinic
      ? mappedSettings
      : mappedSettings.filter((setting) => setting.settingType !== NotificationSettingType.PaymentDispute);
  }, [hasAdminRoleAtCurrentClinic, userData?.findUniqueUser?.userSetting?.notificationSettings]);

  const userSetting = userData?.findUniqueUser?.userSetting;
  const isConversationsFilterApplied = useMemo(
    () => Boolean(userSetting?.browserNotificationFilter),
    [userSetting?.browserNotificationFilter],
  );
  const isAssignedToMeChecked = useMemo(
    () =>
      Boolean(
        userData?.findUniqueUser?.userSetting?.browserNotificationFilter?.assignees?.find(
          ({ id }) => id === clinicUser?.id,
        ),
      ),

    [userData, clinicUser],
  );
  const isUnassignedChecked = useMemo(
    () => Boolean(userSetting?.browserNotificationFilter?.includeUnassigned),
    [userSetting?.browserNotificationFilter?.includeUnassigned],
  );

  const [updateSettings] = useUpdateClinicSettingsNotificationsUserSettingMutation({
    refetchQueries: [getChannelUnreadMessageCount],
  });

  const setAll = useCallback(
    (value: boolean | string | null, prop: keyof UserNotificationSetting): void => {
      if (!userSetting) return;
      const notificationSettingsUpserts = settings
        .filter((setting) => prop !== 'smsEnabled' || setting.settingType !== NotificationSettingType.PaymentDispute)
        .map((setting) => ({
          where: { id: setting.id || '' },
          create: {
            settingType: setting.settingType,
            [prop]: value,
          },
          update: { [prop]: value },
        }));
      updateSettings({
        variables: {
          where: { id: userSetting.id },
          data: {
            notificationSettings: {
              upsert: notificationSettingsUpserts,
            },
          },
        },
      });
    },
    [userSetting, updateSettings, settings],
  );

  const onNotificationEventToggle = (setting: UserNotificationSetting, prop: keyof UserNotificationSetting): void => {
    if (!userSetting) return;
    const notificationSettingsUpdates = {
      [prop]: !setting[prop],
    };
    updateSettings({
      variables: {
        where: { id: userSetting.id },
        data: {
          notificationSettings: {
            upsert: [
              {
                where: { id: setting.id || '' },
                create: {
                  settingType: setting.settingType,
                  ...notificationSettingsUpdates,
                },
                update: notificationSettingsUpdates,
              },
            ],
          },
        },
      },
    });
  };

  const updateBrowserNotificationSettings = useCallback(
    async (isEnabled): Promise<void> => {
      if (!userSetting || !userSetting.notificationSettings?.length) return;

      try {
        await updateSettings({
          variables: {
            where: {
              id: userSetting.id,
            },
            data: {
              notificationSettings: {
                update: userSetting.notificationSettings
                  .filter((set) => !!set.id)
                  .map(({ id }) => ({
                    where: {
                      id,
                    },
                    data: {
                      soundNotificationUrl: DEFAULT_AUDIO_URL,
                      browserNotificationEnabled: isEnabled,
                    },
                  })),
              },
            },
          },
        });
        toast(successToastProps);
      } catch (er) {
        toast({
          status: 'error',
          description: er,
        });
      }
    },
    [userSetting, toast, updateSettings],
  );

  const getBrowserPermissionStatus = useCallback(
    async (shouldReload?: boolean): Promise<NotificationPermission> => {
      // If the permission is `default` we can request permission here, if it is `denied` we let the user know that they have denied permissions
      switch (notificationPermissionStatus) {
        case 'default':
          await Notification.requestPermission().then((permission) => {
            if (permission !== 'granted') {
              setShouldShowPermissionsAlert(true);
              return 'denied';
            } else {
              if (shouldReload) {
                window.location.reload();
              }
              return 'granted';
            }
          });
          return 'granted';
        default:
          return notificationPermissionStatus;
      }
    },
    [notificationPermissionStatus],
  );

  const toggleBrowserNotificationSetting = useCallback(
    async (isEnabled) => {
      const permissionStatus = await getBrowserPermissionStatus(false);

      if (permissionStatus === 'granted' && isEnabled) {
        updateBrowserNotificationSettings(true);
      } else if (permissionStatus === 'denied' && isEnabled) {
        setShouldShowPermissionsAlert(true);
      } else {
        updateBrowserNotificationSettings(isEnabled);
      }
    },
    [getBrowserPermissionStatus, updateBrowserNotificationSettings],
  );

  const toggleBrowserNotificationFilter = useCallback(
    (isFilterApplied: boolean): void => {
      if (!userSetting || !clinicUser) return;
      updateSettings({
        variables: {
          where: {
            id: userSetting.id,
          },
          data: {
            browserNotificationFilter: {
              delete: !isFilterApplied,
              create: isFilterApplied
                ? {
                    assignees: {
                      connect: [{ id: clinicUser.id }],
                    },
                  }
                : undefined,
            },
          },
        },
      });
    },
    [clinicUser, userSetting, updateSettings],
  );

  const handleBrowserNotificationTypeChange = useCallback(
    (value: string) => {
      const isFilterApplied = value !== ALL_CONVERSATIONS;
      toggleBrowserNotificationFilter(isFilterApplied);
      toast(successToastProps);
    },
    [toggleBrowserNotificationFilter, toast],
  );

  const handleAssigneeSelectionChange = useCallback(
    (values: (string | number)[]) => {
      if (!userSetting || !clinicUser) return;
      const updateData: ChannelSelectionUncheckedUpdateWithoutUserSettingInput = {};

      if (!values.length) {
        toggleBrowserNotificationFilter(false /* isFilterApplied */);
        return;
      }

      const includeUnassigned = !!values.find((val) => val === UNASSIGNED_CONVERSATIONS);
      updateData.includeUnassigned = includeUnassigned;

      const includeClinicUser = !!values.find((val) => val === clinicUser.id);
      updateData.assignees = {
        connect: includeClinicUser ? [{ id: clinicUser.id }] : undefined,
        disconnect: !includeClinicUser ? undefined : [{ id: clinicUser.id }],
      };

      updateSettings({
        variables: {
          where: {
            id: userSetting.id,
          },
          data: {
            browserNotificationFilter: {
              upsert: {
                create: {
                  assignees: {
                    connect: [{ id: clinicUser.id }],
                  },
                },
                update: updateData,
              },
            },
          },
        },
      });
    },
    [userSetting, clinicUser, updateSettings, toggleBrowserNotificationFilter],
  );

  const isBrowserNotificationSettingEnabled = useMemo(
    () => settings.some(({ browserNotificationEnabled }) => browserNotificationEnabled),
    [settings],
  );

  const isBrowserNotificationChecked = useMemo((): boolean => {
    return isBrowserNotificationSettingEnabled && notificationPermissionStatus === 'granted';
  }, [isBrowserNotificationSettingEnabled, notificationPermissionStatus]);

  const hasMismatchedPermissions = useMemo(
    () =>
      (notificationPermissionStatus === 'denied' || notificationPermissionStatus === 'default') &&
      isBrowserNotificationSettingEnabled,
    [isBrowserNotificationSettingEnabled, notificationPermissionStatus],
  );

  const assigneeSelectionOptions: CheckboxProps[] = useMemo(
    () => [
      {
        children: 'Me',
        value: clinicUser?.id,
        isChecked: isAssignedToMeChecked,
      },
      {
        children: 'No one (unassigned)',
        value: UNASSIGNED_CONVERSATIONS,
        isChecked: isUnassignedChecked,
      },
    ],
    [clinicUser?.id, isAssignedToMeChecked, isUnassignedChecked],
  );

  const areAllTextSettingsEnabled = useMemo(() => {
    const filteredSettings = settings.filter(
      (setting) => setting.settingType !== NotificationSettingType.PaymentDispute,
    );
    return (hasAdminRoleAtCurrentClinic ? filteredSettings : settings).every(({ smsEnabled }) => smsEnabled);
  }, [hasAdminRoleAtCurrentClinic, settings]);

  return (
    <Box>
      <Text as="p" size="sm" variant="subtle" py={3}>
        Controls to make sure you get notified about what&apos;s important to you.
      </Text>
      <Divider />
      <Box my={6}>
        <Text as="p" fontWeight="semibold" mb={4}>
          In-App Notifications
        </Text>
        <Box>
          <Box>
            <RadioGroup
              label="Notify me about new messages from:"
              direction="column"
              radios={inAppNotificationOptions}
              value={isConversationsFilterApplied ? ASSIGNED_CONVERSATIONS : ALL_CONVERSATIONS}
              onChange={(val): void => handleBrowserNotificationTypeChange(val)}
            />
            <Box mt={1} ml={6}>
              <CheckboxGroup
                direction="column"
                size="sm"
                checkboxes={assigneeSelectionOptions}
                value={[
                  ...(isAssignedToMeChecked && clinicUser ? [clinicUser?.id] : []),
                  ...(isUnassignedChecked ? [UNASSIGNED_CONVERSATIONS] : []),
                ]}
                onChange={(values): void => handleAssigneeSelectionChange(values)}
                isDisabled={!isConversationsFilterApplied}
              />
            </Box>
          </Box>
        </Box>
        <Box mt={5}>
          <HStack align="center" justify="flex-start">
            <Text>Browser and sound notifications</Text>
            <Popover
              size="lg"
              trigger="hover"
              placement="top"
              anchor={
                <Flex cursor="pointer">
                  <Icon name="questionMarkCircle" size="sm" />
                </Flex>
              }
            >
              <Text size="sm" variant="onContrast" lineHeight="normal">
                When the toggle is set to <b>on</b>, a sound will play and a browser notification will be sent when a
                message arrives in a conversation based on your selection above, even if you&apos;re browsing in another
                tab or using a different application.
              </Text>
            </Popover>
            <Switch
              isDisabled={hasMismatchedPermissions}
              isFieldInline
              isChecked={isBrowserNotificationChecked}
              onChange={(): Promise<void> => toggleBrowserNotificationSetting(!isBrowserNotificationChecked)}
            />
          </HStack>
          {(hasMismatchedPermissions || shouldShowPermissionsAlert) && (
            <>
              <Alert
                status="warning"
                hideCloseButton
                mt={2}
                maxW="700px"
                title={`Your browser's notification permissions ${
                  notificationPermissionStatus === 'denied' ? 'have been denied' : 'are not enabled'
                }`}
                {...(notificationPermissionStatus === 'default' && {
                  buttonProps: {
                    buttonText: 'Update Browser Permissions',
                    onClick: (): Promise<NotificationPermission> => getBrowserPermissionStatus(true),
                    buttonPosition: 'bottomLeft',
                  },
                })}
              >
                {notificationPermissionStatus === 'default' && (
                  <Text>
                    This toggle is off because you have not granted your browser permission to notify you of updates
                    within this application.
                  </Text>
                )}
                {notificationPermissionStatus === 'denied' && (
                  <>
                    <Text>
                      This toggle is off because you have denied your browser permission to notify you of updates within
                      this application. Please udpate your permissions, and refresh the page.
                    </Text>
                  </>
                )}
              </Alert>
              {isImpersonating && (
                <Alert status="info" hideCloseButton mt={2} maxW="700px">
                  <Text>
                    {`If you are impersonating a clinic member, this warning is specific to your own browser and does not
                    reflect the clinic member's browser-specific settings.`}
                  </Text>
                </Alert>
              )}
            </>
          )}
        </Box>
      </Box>
      <Divider />
      <Box my={6}>
        <Text as="p" fontWeight="semibold" mb={4}>
          Email &amp; Text Notifications
        </Text>
        <Flex w="100%" alignItems="left" justifyContent="space-between" py={3} px={2} bg="background.default">
          <Box flex={1}>
            <Text>Event</Text>
          </Box>
          <Box flex={1}>
            <Checkbox
              isChecked={settings.every(({ emailEnabled }) => emailEnabled)}
              onChange={(): void => {
                trackEvent(SettingsMyNotificationEvents.EnableAllEmailNotifs, {
                  EmailNotification: !settings.every(({ emailEnabled }) => emailEnabled),
                });
                setAll(!settings.every(({ emailEnabled }) => emailEnabled), 'emailEnabled');
                toast(successToastProps);
              }}
            >
              Email
            </Checkbox>
          </Box>
          <Box flex={1}>
            <Checkbox
              isChecked={areAllTextSettingsEnabled}
              onChange={(): void => {
                trackEvent(SettingsMyNotificationEvents.EnableAllTextNotifs, {
                  TextNotification: !settings.every(({ smsEnabled }) => smsEnabled),
                });
                setAll(!areAllTextSettingsEnabled, 'smsEnabled');
                toast(successToastProps);
              }}
            >
              Text (SMS)
            </Checkbox>
          </Box>
        </Flex>
        <Divider />
        {settings.map((setting) => (
          <Flex
            key={setting.settingType}
            w="100%"
            alignItems="left"
            justifyContent="space-between"
            py={3}
            px={2}
            _even={{ bg: 'background.default' }}
          >
            <Box flex={1}>
              <Flex alignItems="center">
                <Text size="sm" fontWeight="semibold">
                  {notificationTypeDisplayMapper[setting.settingType]}
                </Text>
                {setting.settingType === NotificationSettingType.ConnectWidgetRequestSubmitted && (
                  <Popover
                    size="lg"
                    trigger="hover"
                    placement="top"
                    anchor={
                      <Flex as="span" cursor="pointer" ml={1}>
                        <Icon name="questionMarkCircle" size="sm" />
                      </Flex>
                    }
                  >
                    <Text variant="onContrast" size="sm">
                      {
                        "You'll receive this notification only for those Website Widget request options that are assigned to you, as configured on the "
                      }
                      <Link
                        as={ReactRouterLink}
                        variant="interactiveOnContrast"
                        to={`${RouteDefinitions.Settings}${SettingsRoutes.ClinicWidget}`}
                        size="sm"
                      >
                        Website Widget settings page
                      </Link>
                      .
                    </Text>
                  </Popover>
                )}
              </Flex>
            </Box>
            <Box flex={1}>
              <Checkbox
                onChange={(): void => {
                  trackEvent(SettingsMyNotificationEvents.EnableEmailNotifsPerFeature, {
                    feature: notificationTypeDisplayMapper[setting.settingType],
                    EmailNotification: !setting.emailEnabled,
                  });
                  onNotificationEventToggle(setting, 'emailEnabled');
                  toast(successToastProps);
                }}
                isChecked={setting.emailEnabled}
              />
            </Box>
            <Box flex={1}>
              <Checkbox
                onChange={(): void => {
                  trackEvent(SettingsMyNotificationEvents.EnableTextNotifsPerFeature, {
                    feature: notificationTypeDisplayMapper[setting.settingType],
                    TextNotification: !setting.emailEnabled,
                  });

                  onNotificationEventToggle(setting, 'smsEnabled');
                  toast(successToastProps);
                }}
                isChecked={setting.settingType !== NotificationSettingType.PaymentDispute ? setting.smsEnabled : false}
                {...(setting.settingType === NotificationSettingType.PaymentDispute && { isDisabled: true })}
              />
            </Box>
          </Flex>
        ))}
      </Box>
    </Box>
  );
};

export default MyNotificationsSettings;
